import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Sparkles, Loader2, RefreshCw } from 'lucide-react';
import { axios, auth, mbUrl, errMsg, muted, primary, toast, ORIGIN_LABEL } from './mbApi';
import { SuggestionCard, Evidence } from './SuggestionCard';

const ELIG_CLS = {
  metadata_belum_lengkap: 'text-amber-400', belum_cukup_data: 'text-amber-400', data_bermasalah: 'text-red-400',
  berpotensi_variasi: 'text-emerald-400', perlu_ujian_pengesahan: 'text-sky-400',
};
const LABEL = {
  metadata_belum_lengkap: 'Metadata belum lengkap', belum_cukup_data: 'Belum cukup data', data_bermasalah: 'Data bermasalah',
  berpotensi_variasi: 'Berpotensi untuk variasi', perlu_ujian_pengesahan: 'Perlu ujian pengesahan',
};

const STALE_MS = 5 * 60 * 1000;
const isLive = (a) => a?.status === 'pending' && Date.now() - new Date(a.started_at).getTime() < STALE_MS;

const Eligibility = ({ c, e }) => (
  <div className="space-y-0.5" data-testid={`mb-analyst-elig-${c.id}`}>
    <p className={`text-xs font-semibold ${ELIG_CLS[e.status]}`}>Kelayakan: {LABEL[e.status]}</p>
    {(e.reasons || []).map((r) => <p key={r} className={`text-[11px] ${muted}`}>• {r}</p>)}
    {(e.fixes || []).map((f) => <p key={f} className="text-[11px] text-sky-400">→ {f}</p>)}
  </div>
);

const Output = ({ a }) => a?.output && (
  <div className={`text-[11px] ${muted} space-y-1`} data-testid="mb-analyst-output">
    <p className={a.model === 'demo-fixture' ? 'text-amber-400' : ''} data-testid="mb-analyst-origin">
      {a.model === 'demo-fixture' ? 'Analisis CONTOH (demo) — bukan AI sebenar' : `AI ${a.model || ''}`} · Asal data: {ORIGIN_LABEL[a.data_origin || a.snapshot?.data_origin] || 'belum disahkan'}
      {a.snapshot?.date_from ? ` · ${a.snapshot.date_from} → ${a.snapshot.date_to} (${a.snapshot.timezone || 'MYT'})` : ''}
    </p>
    {(a.output.observations || []).map((o) => <p key={o.text}>• {o.text}{o.refs?.length ? ` [${o.refs.join(', ')}]` : ''}</p>)}
    {(a.output.keep || []).length > 0 && <p><span className={primary}>Disyorkan kekal:</span> {a.output.keep.join(' · ')}</p>}
  </div>
);

export const AnalystPanel = ({ ibId, c, canRun, canBrief }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const timer = useRef(null);
  const load = useCallback(async () => {
    const { data: d } = await axios.get(mbUrl(ibId, `/creatives/${c.id}/analysis`), auth());
    setData(d);
    return d;
  }, [ibId, c.id]);
  const poll = useCallback((aid, n = 0) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const { data: r } = await axios.get(mbUrl(ibId, `/analyst/analyses/${aid}`), auth());
        if (r.analysis.status === 'pending') {
          if (n < 40) { poll(aid, n + 1); return; }
          toast.message('AI masih menganalisis lebih lama dari biasa — tutup & buka semula panel ini dalam beberapa minit');
        }
        await load();
        if (r.analysis.status === 'gagal') toast.error(r.analysis.error || 'Analisis gagal');
      } catch (e) { setErr(`${errMsg(e, 'Semakan status analisis gagal')} — tekan Muat semula status`); }
    }, 4000);
  }, [ibId, load]);
  useEffect(() => () => clearTimeout(timer.current), []);
  const refresh = async () => {
    setErr('');
    try { const d = await load(); if (isLive(d.analysis)) poll(d.analysis.id); } catch (e) { setErr(errMsg(e, 'Gagal load analisis')); }
  };
  const toggle = () => {
    if (!open) refresh();
    setOpen(!open);
  };
  const run = async () => {
    setBusy(true);
    try {
      const { data: r } = await axios.post(mbUrl(ibId, `/creatives/${c.id}/analyze`), {}, auth());
      if (r.status === 'tidak_layak') toast.message('Belum layak dianalisis — lihat sebab di bawah');
      else if (r.cached) toast.success('Analisis sama sudah ada (data tak berubah) — tiada panggilan AI baharu');
      await load();
      if (r.status === 'pending') poll(r.analysis.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Gagal mulakan analisis'); } finally { setBusy(false); }
  };
  const a = data?.analysis;
  const pending = isLive(a);
  return (
    <div className="space-y-2" data-testid={`mb-analyst-${c.id}`}>
      <button className="text-sm underline flex items-center gap-1.5 text-[#F77F00] min-h-10" onClick={toggle} data-testid={`mb-analyst-toggle-${c.id}`}>
        <Sparkles size={15} /> {open ? 'Tutup analisis' : 'Analisis & Cadang Variasi'}
      </button>
      {open && err && (
        <div className="text-sm text-red-400 flex flex-wrap items-center gap-2" data-testid={`mb-analyst-load-error-${c.id}`}>
          <span>{err}</span>
          <Button size="sm" variant="outline" className="h-10" onClick={refresh} data-testid={`mb-analyst-refresh-${c.id}`}>Muat semula status</Button>
        </div>
      )}
      {open && !data && !err && <p className={`text-sm ${muted}`} data-testid={`mb-analyst-loading-${c.id}`}>Memuat analisis…</p>}
      {open && data && (
        <div className="rounded-lg border border-[#F77F00]/30 p-2 sm:p-3 space-y-3" data-testid={`mb-analyst-body-${c.id}`}>
          <Eligibility c={c} e={data.eligibility} />
          <p className={`text-[11px] ${muted}`}><span className={primary}>Bukti semasa:</span> <Evidence snap={data.snapshot} baseline={{ min_myr: data.cfg.baseline_min_myr, max_myr: data.cfg.baseline_max_myr, banding: 'CPS unik' }} /></p>
          {canRun && (
            <Button size="sm" variant="outline" className="h-10" disabled={busy || pending || !data.eligibility.eligible} onClick={run} data-testid={`mb-analyst-run-${c.id}`}>
              {busy || pending ? <><Loader2 size={12} className="mr-1 animate-spin" /> AI sedang menganalisis…</> : <><RefreshCw size={12} className="mr-1" /> {a ? 'Analisis semula' : 'Jalankan analisis'}</>}
            </Button>
          )}
          {canRun && a?.status === 'berjaya' && data.eligibility.eligible && (
            <p className={`text-[10px] ${muted}`} data-testid={`mb-analyst-cache-note-${c.id}`}>
              {data.snapshot?.demo
                ? 'Channel demo: analisis semula guna hasil contoh — AI sebenar tidak dipanggil dan tiada caj.'
                : 'Analisis semula: jika angka iklan, keputusan Terima/Tolak dan pembelajaran tak berubah, hasil lama dipapar semula tanpa caj AI. Jika ada perubahan, AI dipanggil semula (ada caj) supaya tidak ulang idea yang dah ditolak.'}
            </p>
          )}
          {!data.eligibility.eligible && <p className={`text-[10px] ${muted}`}>AI tidak dipanggil selagi data belum layak — betulkan perkara di atas dahulu.</p>}
          {a?.status === 'pending' && !pending && <p className="text-[11px] text-amber-400" data-testid={`mb-analyst-stale-${c.id}`}>Analisis sebelum ini tergendala. Tekan analisis semula untuk cuba lagi.</p>}
          {a?.status === 'gagal' && <p className="text-[11px] text-red-400" data-testid={`mb-analyst-error-${c.id}`}>Analisis gagal: {a.error}. Tekan analisis semula untuk cuba lagi.</p>}
          <Output a={a?.status === 'berjaya' ? a : null} />
          {(data.suggestions || []).map((s) => <SuggestionCard key={s.id} ibId={ibId} s={s} canBrief={canBrief} reload={load} />)}
        </div>
      )}
    </div>
  );
};
